import type { User } from "@/types/user";

export type CheckoutPlan = "go" | "business";
export type BillingPeriod = "monthly" | "annual";

/** Stripe Payment Links differ between test and live mode, so they come from the environment. */
const CHECKOUT_LINKS: Record<CheckoutPlan, Record<BillingPeriod, string | undefined>> = {
  go: {
    monthly: import.meta.env.VITE_STRIPE_LINK_GO_MONTHLY,
    annual: import.meta.env.VITE_STRIPE_LINK_GO_ANNUAL,
  },
  business: {
    monthly: import.meta.env.VITE_STRIPE_LINK_BUSINESS_MONTHLY,
    annual: import.meta.env.VITE_STRIPE_LINK_BUSINESS_ANNUAL,
  },
};

const PORTAL_URL = import.meta.env.VITE_STRIPE_PORTAL_URL;

/** Null for a malformed base, so a bad env value reads as "not configured". */
function withParams(
  base: string | undefined,
  params: Record<string, string>
): string | null {
  if (!base) return null;
  let url: URL;
  try {
    url = new URL(base);
  } catch {
    return null;
  }
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, value);
  }
  return url.toString();
}

/** `client_reference_id` is how the backend knows which organization paid. */
export function getCheckoutUrl(
  plan: CheckoutPlan,
  period: BillingPeriod,
  user: User
): string | null {
  return withParams(CHECKOUT_LINKS[plan][period], {
    client_reference_id: user.organization_id,
    prefilled_email: user.email,
  });
}

/**
 * Stripe emails the portal sign-in link to the address on the Stripe customer,
 * which is the one they paid with.
 */
export function getPortalUrl(user: User): string | null {
  return withParams(PORTAL_URL, { prefilled_email: user.email });
}
